import { useMemo } from 'react';

import { Column, Typography, Box, Stack } from '@cast/design-system';

import { useRules, useAbilityOverrides } from '@cast/infrastructure-abilities';
import { Subjects, Actions } from '@cast/shared-ability-data-access';

import { boolToToggleValue, Toggles } from '../../_components/Toggles';
import { ToolboxHeader } from '../../_components/ToolboxHeader';

import {
  SearchProvider,
  SearchableTable,
  Search,
} from '@cast/shared-search-ui';

type Ability = {
  origEnabled: boolean;
  overrideEnabled?: boolean;
};

type RowModel = {
  subject: string;
  create?: Ability;
  view?: Ability;
  update?: Ability;
  delete?: Ability;
};

type ActionCellProps = RowModel & {
  action: 'create' | 'view' | 'update' | 'delete';
};

const ActionCell = (props: ActionCellProps) => {
  const ability = props[props.action];

  const origEnabled = !!ability?.origEnabled;
  const overrideEnabled = ability?.overrideEnabled;

  const globalOverride = useAbilityOverrides(
    ({ globalOverride }) => globalOverride
  );

  if (!ability) {
    return;
  }

  return (
    <Stack direction="row" alignItems="center">
      <Stack direction="row" width={80}>
        <Typography variant="P12M" pr={2}>
          Orig:{' '}
        </Typography>
        <Typography variant="A2" color={origEnabled ? 'green.500' : 'red.400'}>
          {origEnabled ? 'Allowed' : 'Denied'}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center">
        <Typography variant="P12M" pr={2}>
          Override:{' '}
        </Typography>
        <Toggles
          value={boolToToggleValue(overrideEnabled)}
          onChange={(value) => {
            const state = useAbilityOverrides.getState();
            if (value === 'none') {
              state.clearOverride?.(props.subject as Subjects, props.action);
            } else {
              state.override(
                props.subject as Subjects,
                props.action,
                value === 'allowed'
              );
            }
          }}
          disabled={globalOverride !== undefined}
        />
      </Stack>
    </Stack>
  );
};

export const AbilityOverrides = () => {
  const rules = useRules();

  const overrides = useAbilityOverrides(({ overrides }) => overrides);
  const globalOverride = useAbilityOverrides(
    ({ globalOverride }) => globalOverride
  );

  const rowModels = useMemo(() => {
    const subjectMap = new Map<string, RowModel>();

    rules.forEach((rule) => {
      const { subject, action } = rule as {
        subject: Subjects;
        action: Actions;
      };
      const override = overrides?.[subject]?.[action];

      if (!subjectMap.has(subject)) {
        subjectMap.set(subject, { subject });
      }

      const rowModel = subjectMap.get(subject)!;
      const ability: Ability = {
        origEnabled: !rule.inverted,
        overrideEnabled: override,
      };

      switch (action) {
        case 'create':
          rowModel.create = ability;
          break;
        case 'view':
          rowModel.view = ability;
          break;
        case 'update':
          rowModel.update = ability;
          break;
        case 'delete':
          rowModel.delete = ability;
          break;
      }
    });

    return Array.from(subjectMap.values());
  }, [rules, overrides]);

  return (
    <Stack height="100%">
      <SearchProvider data={rowModels}>
        <ToolboxHeader>
          <Stack direction="row" alignItems="flex-end">
            <Search size="small" />
            <Typography variant="P12B" pl={12} pr={8}>
              Global:
            </Typography>
            <Toggles
              value={boolToToggleValue(globalOverride)}
              onChange={(value) => {
                const state = useAbilityOverrides.getState();
                if (value === 'none') {
                  state.clearGlobalOverride();
                } else {
                  state.setGlobalOverride(value === 'allowed');
                }
              }}
            />
          </Stack>
        </ToolboxHeader>
        <Box flexGrow={1}>
          <SearchableTable fillParentHeight>
            <Column
              id="subject"
              width={400}
              header="Subject"
              renderCell="subject"
            />
            <Column<RowModel>
              id="view"
              minWidth={300}
              header="View"
              renderCell={(rowModel) =>
                rowModel.view && <ActionCell {...rowModel} action="view" />
              }
            />
            <Column<RowModel>
              id="create"
              minWidth={300}
              header="Create"
              renderCell={(rowModel) =>
                rowModel.create && <ActionCell {...rowModel} action="create" />
              }
            />
            <Column<RowModel>
              id="update"
              minWidth={300}
              header="Update"
              renderCell={(rowModel) =>
                rowModel.update && <ActionCell {...rowModel} action="update" />
              }
            />
            <Column<RowModel>
              id="delete"
              minWidth={300}
              header="Delete"
              renderCell={(rowModel) =>
                rowModel.delete && <ActionCell {...rowModel} action="delete" />
              }
            />
          </SearchableTable>
        </Box>
      </SearchProvider>
    </Stack>
  );
};
